/* CUSTOM — Product list category filters

   Every card is already on the page; filtering is a class toggle and nothing
   else. With nine products there is no case for refetching a section over the
   network to hide six cards.

   Each card carries `data-mg-in` — the indices of the filter blocks it belongs
   to, worked out in Liquid. The script never inspects a product; it compares
   strings. A card can belong to several categories, which is why this is a list
   rather than a single value. */

class MgProductList extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-mg-grid]');
    this.items = Array.from(this.querySelectorAll('[data-mg-item]'));
    this.buttons = Array.from(this.querySelectorAll('[data-mg-filter]'));
    this.countEl = this.querySelector('[data-mg-count]');
    this.emptyEl = this.querySelector('[data-mg-empty]');
    if (!this.grid || !this.items.length) return;

    this.countTemplate = this.dataset.countTemplate || '';

    this.buttons.forEach((btn) => {
      btn.addEventListener('click', () => this.#apply(btn.dataset.mgFilter));
    });

    // Announce the starting count without waiting for an interaction.
    this.#apply('all', { silent: true });

    // A jump link elsewhere on the page can arrive with a category already
    // chosen — the hero's chips do. Delegated on the document rather than bound
    // to the links, because the hero is a different section and neither should
    // have to know the other exists. The link's own `href` does the scrolling.
    this.onExternal = (event) => {
      const trigger = event.target.closest('[data-mg-filter-label]');
      if (!trigger) return;
      const wanted = (trigger.dataset.mgFilterLabel || '').trim().toLowerCase();
      const match = this.buttons.find((b) => b.textContent.trim().toLowerCase() === wanted);
      if (match) this.#apply(match.dataset.mgFilter, { silent: true });
    };
    document.addEventListener('click', this.onExternal);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onExternal);
  }

  #matches(item, key) {
    if (key === 'all') return true;
    // Space-delimited so `1` never matches `11`. Padding both sides is cheaper
    // and clearer than splitting on every card, every click.
    return ` ${item.dataset.mgIn || ''} `.includes(` ${key} `);
  }

  #apply(key, { silent = false } = {}) {
    let shown = 0;
    this.items.forEach((item) => {
      const show = this.#matches(item, key);
      // `hidden` rather than a class: it is the browser's own "not here", so the
      // card leaves the accessibility tree and the tab order with it, and a
      // quick-add button inside a hidden card cannot be tabbed to.
      item.hidden = !show;
      if (show) shown += 1;
    });

    this.buttons.forEach((btn) => {
      const on = btn.dataset.mgFilter === key;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', String(on));
    });

    if (this.emptyEl) this.emptyEl.hidden = shown > 0;
    if (this.countEl && this.countTemplate) {
      this.countEl.textContent = this.countTemplate.replace('[count]', shown);
    }

    // The heading is what the eye returns to when the grid changes under it, and
    // on a phone the grid can start below the fold after a filter shortens it.
    if (!silent) {
      const head = this.closest('.mg-section')?.querySelector('.mg-section__head');
      const anchor = head || this;
      const top = anchor.getBoundingClientRect().top + window.scrollY;
      const style = getComputedStyle(anchor);
      const margin = parseFloat(style.scrollMarginTop) || 0;
      if (top - margin < window.scrollY) {
        window.scrollTo({
          left: 0,
          top: top - margin,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
    }
  }
}

if (!customElements.get('mg-product-list')) customElements.define('mg-product-list', MgProductList);
